/**
 * The glyphs the visit boards draw, lifted from their HTML
 * (`design/mockups/50-visit-page/html/vp-17-quote-to-visits.html`) — the
 * same pattern as `frame/mark-glyphs.tsx`: the paths are data, so the board's
 * own triangle, tick and arrows are drawn rather than the nearest lucide one
 * (⛔: *a different icon* is a defect). Real elements; no markup is ever
 * injected as a string.
 */

interface GlyphSpec {
  paths: string[]
  circles?: { cx: number; cy: number; r: number }[]
}

export const VISIT_GLYPHS = {
  /** Board 17 E, G, H: the warning triangle. */
  warn: { paths: ['M12 4l9 16H3z', 'M12 10v4M12 17v.01'] },
  /** Board 17 C: the tick in the green circle; F: the ticked box. */
  check: { paths: ['M5 12.5l4.5 4.5L19 7.5'] },
  /** Board 17 E: Create the missing visit. */
  plus: { paths: ['M12 5v14M5 12h14'] },
  /** Board 17 G: Try again. */
  retry: { paths: ['M20 11a8 8 0 0 0-14-4L4 9M4 4v5h5M4 13a8 8 0 0 0 14 4l2-2M20 20v-5h-5'] },
  /** Board 17 F: the tile and the ✕. */
  close: { paths: ['M6 6l12 12M18 6L6 18'] },
  /** Board 17 F: the amber band's circle-i; Board 10: *Finished photos come on the last day*. */
  info: { paths: ['M12 11v6M12 7.5v.01'], circles: [{ cx: 12, cy: 12, r: 9 }] },
  /** Board 9: the composer's attach button. */
  attach: { paths: ['M21 11.5l-8.6 8.6a5.2 5.2 0 0 1-7.4-7.4l8.6-8.6a3.5 3.5 0 0 1 5 5l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9'] },
  /** Board 9: a note's **Edit**. */
  pencil: { paths: ['M4 20l1-4L16.5 4.5a2 2 0 0 1 3 3L8 19z', 'M14 7l3 3'] },
  /** Board 9: *Edit on the property* · *Open J-3271*. */
  chevronRight: { paths: ['M9 6l6 6-6 6'] },
  /** Board 9: the internal-notes line (the board's `rect` as a path). */
  lock: { paths: ['M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z', 'M8 11V8a4 4 0 0 1 8 0v3'] },
  /** Board 9: the crew iPad's **Navigate**. */
  navigate: { paths: ['M3 11l19-9-9 19-2-8-8-2z'] },
  /** Board 9: **Plans · 3**; Board 10 B: **Add to the job’s files**. */
  doc: { paths: ['M6 3h8l4 4v14H6z', 'M14 3v4h4M9 12h6M9 16h6'] },
  /** Board 9: **Map · 2**. */
  map: { paths: ['M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z', 'M9 4v14M15 6v14'] },
  /** Board 9: **Load list**. */
  truck: {
    paths: ['M3 6h11v10H3zM14 10h4l3 3v3h-7'],
    circles: [{ cx: 7, cy: 18, r: 1.8 }, { cx: 17, cy: 18, r: 1.8 }],
  },
  /** Board 10: **Upload**. */
  upload: { paths: ['M12 16V5M7 9l5-5 5 5M5 20h14'] },
  /** Board 10 B: *Taken at the site*. */
  pin: { paths: ['M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z'], circles: [{ cx: 12, cy: 10, r: 2.5 }] },
  /** Board 10 B: **Download**. */
  download: { paths: ['M12 4v11M7 11l5 5 5-5M5 20h14'] },
  /** Boards 2, 4, 16: a material line (the Materials tab's own glyph). */
  material: {
    paths: ['M12 10v2M9 14l1-2M15 14l-1-2'],
    circles: [{ cx: 12, cy: 7, r: 3 }, { cx: 7, cy: 16, r: 3 }, { cx: 17, cy: 16, r: 3 }],
  },
  /** Boards 2, 3, 4, 18: **Order**, a PO, *Order what’s needed*. */
  cart: { paths: ['M3 4h3l2.5 11h10l2-8H7'], circles: [{ cx: 9, cy: 20, r: 1.4 }, { cx: 18, cy: 20, r: 1.4 }] },
  /** Boards 2, 4: **Swap** and the *Swapped* tag. */
  swap: { paths: ['M7 7h12l-3-3M17 17H5l3 3'] },
  /** Boards 2, 3, 18 C: *From the yard* · *Order · delivered to the yard* · *Load at the yard*. */
  yard: { paths: ['M3 21V9l9-5 9 5v12', 'M7 21v-7h10v7M7 17h10'] },
  /** Boards 2, 3, 18 C: *Order · delivered to the site* · *Already on site*. */
  site: { paths: ['M3 20h18M5 20V10l7-5 7 5v10', 'M10 20v-5h4v5'] },
  /** Boards 2, 3, 18 C: *Order · crew picks up* · *Pick up on the way*. */
  pickup: {
    paths: ['M3 7h11v9H3zM14 10h4l3 3v3h-7', 'M6 10h5'],
    circles: [{ cx: 7, cy: 18, r: 1.8 }, { cx: 17, cy: 18, r: 1.8 }],
  },
  /** Boards 2, 3: *Bought in the field*. */
  receipt: { paths: ['M6 3h12v18l-3-2-3 2-3-2-3 2z', 'M9 8h6M9 12h6'] },
  /** Board 3: a line on its way (*arriving today, about 2 PM*). */
  clock: { paths: ['M12 7v5l3 2'], circles: [{ cx: 12, cy: 12, r: 9 }] },
  /** Board 3 B: the same-trip band. */
  sparkle: { paths: ['M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z', 'M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z'] },
  /** Board 3 C: the toast's tick. */
  checkCircle: { paths: ['M8 12.5l3 3 5-6'], circles: [{ cx: 12, cy: 12, r: 9 }] },
  /** Board 4 C: *Ask Kyle Brandt to cancel the line*. */
  people: {
    paths: ['M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5', 'M17 14.5c2.4 0 4 1.6 4 4'],
    circles: [{ cx: 9, cy: 8, r: 3.2 }, { cx: 17, cy: 9, r: 2.5 }],
  },
  /** Boards 2, 18 C: a line's ⋯. */
  more: { paths: [], circles: [{ cx: 12, cy: 5, r: 1.2 }, { cx: 12, cy: 12, r: 1.2 }, { cx: 12, cy: 19, r: 1.2 }] },
  /** Board 2: *Find a material*. */
  search: { paths: ['M20 20l-3.5-3.5'], circles: [{ cx: 11, cy: 11, r: 7 }] },
  /** Board 3 B: a picker's chevron (*Picks up*). */
  chevronDown: { paths: ['M6 9l6 6 6-6'] },
  /** Boards 5, 8, 18 G: company equipment (the Equipment tab's own glyph). */
  loader: {
    paths: ['M4 17h12M6 17V9h6l3 4M15 13h5l-1 4'],
    circles: [{ cx: 7, cy: 18, r: 1.8 }, { cx: 16, cy: 18, r: 1.8 }],
  },
  /** Boards 5, 18 I: the vendor block on a rental card (a building). */
  vendor: { paths: ['M3 21h18M5 21V4h14v17M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2'] },
  /** Board 11: **Move this day**, the Add a day and Move tiles, **Move the day** (the board's `rect` as a path). */
  calendar: { paths: ['M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z', 'M3 10h18M8 3v4M16 3v4'] },
  /** Board 11 C: *Company equipment* in What moves with it. */
  tool: { paths: ['M14 5l5 5-9 9H5v-5zM12 7l5 5'] },
  /** Board 11 C: the customer's text in What moves with it. */
  bubble: { paths: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'] },
  /** Board 18 D, F: the close-out's tile and **Close out for the crew** (the board's `rect` as a path). */
  clipboard: {
    paths: ['M8 4h8a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z', 'M9 4V3h6v1M9 10h6M9 14h6M9 18h3'],
  },
  /** Board 1 (Slice 1): the header's job link — the layers the nav's Jobs row draws. */
  job: { paths: ['M12 3l9 5-9 5-9-5z', 'M3 11l9 5 9-5M3 15l9 5 9-5'] },
  /** Board 1 (Slice 1): the header's contact — one person. */
  person: { paths: ['M5 20c0-3.6 3-6 7-6s7 2.4 7 6'], circles: [{ cx: 12, cy: 8, r: 3.5 }] },
  /** Board 1 (Slice 1): People's **Call**, the phone's *Call Marco*. */
  phone: { paths: ['M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z'] },
  /** Board 1 (Slice 1): For the crew's **Preview**. */
  eye: { paths: ['M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z'], circles: [{ cx: 12, cy: 12, r: 3 }] },
  /** Board 13 B (Slice 1): **Take off hold**. */
  play: { paths: ['M7 5l12 7-12 7z'] },
  /** Board 16 (Slice 1): the phone's **Photo** — a camera. */
  camera: {
    paths: ['M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z'],
    circles: [{ cx: 12, cy: 13, r: 3.5 }],
  },
} satisfies Record<string, GlyphSpec>

export type VisitGlyphName = keyof typeof VISIT_GLYPHS

/** One glyph, at the size and stroke the board draws it, in the current colour. */
export function VisitGlyph({ name, size, stroke }: { name: VisitGlyphName; size: number; stroke: number }) {
  const glyph: GlyphSpec = VISIT_GLYPHS[name]
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      className="shrink-0"
      style={{
        stroke: 'currentColor', fill: 'none', strokeWidth: stroke, strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    >
      {glyph.circles?.map((circle) => (
        <circle key={`${circle.cx}-${circle.cy}`} cx={circle.cx} cy={circle.cy} r={circle.r} />
      ))}
      {glyph.paths.map((path) => <path key={path} d={path} />)}
    </svg>
  )
}
